/* KönigPanda navigation: mobile drawer, desktop mega menus, mobile search toggle.
   No dependencies. Loaded with `defer`. */
(() => {
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  /* ---------------------------------------------------------------------
     Scroll lock that preserves the scroll position (iOS-safe).          */
  const scrollLock = {
    y: 0,
    locked: false,
    lock() {
      if (this.locked) return;
      this.y = window.scrollY;
      document.documentElement.classList.add('kp-scroll-lock');
      document.body.style.top = `-${this.y}px`;
      this.locked = true;
    },
    unlock() {
      if (!this.locked) return;
      document.documentElement.classList.remove('kp-scroll-lock');
      document.body.style.top = '';
      window.scrollTo(0, this.y);
      this.locked = false;
    },
  };

  const trapFocus = (container, event) => {
    const items = [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ---------------------------------------------------------------------
     <kp-drawer> — mobile menu dialog with drill-down views.             */
  class KpDrawer extends HTMLElement {
    connectedCallback() {
      // Portal to <body> so no header stacking context or transform can clip it.
      if (this.constructor.portal && this.parentElement !== document.body) {
        document.querySelectorAll(`body > ${this.localName}#${this.id}`).forEach((old) => old !== this && old.remove());
        document.body.appendChild(this);
        return;
      }
      if (this.initialised) return;
      this.initialised = true;
      this.panel = this.querySelector('[role="dialog"]');
      this.onKeydown = this.onKeydown.bind(this);
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-kp-drawer-close]')) this.close();
        const opener = event.target.closest('[data-kp-view-open]');
        if (opener) this.showView(opener.dataset.kpViewOpen, opener);
        if (event.target.closest('[data-kp-view-back]')) this.showView('root');
      });
    }

    open(trigger) {
      clearTimeout(this.closeTimer);
      document.querySelectorAll('kp-drawer.is-open, kp-filter-drawer.is-open, kp-cart-drawer.is-open').forEach((other) => {
        if (other !== this) other.close();
      });
      this.trigger = trigger || document.activeElement;
      this.hidden = false;
      this.showView('root', null, false);
      scrollLock.lock();
      if (this.constructor.portal) document.body.classList.add('mobile-menu-active');
      document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        const close = this.querySelector('[data-kp-drawer-close].kp-icon-btn');
        (close || this.panel).focus();
      });
      document.addEventListener('keydown', this.onKeydown);
    }

    close() {
      clearTimeout(this.closeTimer);
      this.classList.remove('is-open');
      document.removeEventListener('keydown', this.onKeydown);
      document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
      document.body.classList.remove('mobile-menu-active');
      scrollLock.unlock();
      let finished = false;
      const finish = () => {
        if (finished || this.classList.contains('is-open')) return;
        finished = true;
        this.hidden = true;
        if (!document.querySelector('kp-drawer.is-open, kp-filter-drawer.is-open, kp-cart-drawer.is-open') && this.trigger && document.contains(this.trigger)) this.trigger.focus();
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
      else this.panel.addEventListener('transitionend', finish, { once: true });
      // Fallback in case transitionend never fires (e.g. display changes).
      this.closeTimer = setTimeout(finish, 400);
    }

    showView(id, opener, moveFocus = true) {
      const views = this.querySelectorAll('[data-kp-view]');
      views.forEach((view) => { view.hidden = view.dataset.kpView !== id; });
      this.querySelectorAll('[data-kp-view-open]').forEach((btn) => btn.setAttribute('aria-expanded', String(btn.dataset.kpViewOpen === id)));
      if (id !== 'root') this.lastOpener = opener;
      if (!moveFocus) return;
      if (id === 'root') {
        if (this.lastOpener) this.lastOpener.focus();
      } else {
        const back = this.querySelector(`[data-kp-view="${id}"] [data-kp-view-back]`);
        if (back) back.focus();
      }
      const body = this.querySelector('.kp-drawer__body');
      if (body) body.scrollTop = 0;
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (event.key === 'Tab') {
        trapFocus(this.panel, event);
      }
    }
  }

  KpDrawer.portal = true;
  window.KpDrawer = KpDrawer;
  if (!customElements.get('kp-drawer')) customElements.define('kp-drawer', KpDrawer);

  /* Filter drawer: same dialog behaviour, rendered inside the results section
     (so the Section Rendering API can refresh it) instead of portalled. */
  class KpFilterDrawer extends KpDrawer {}
  KpFilterDrawer.portal = false;
  if (!customElements.get('kp-filter-drawer')) customElements.define('kp-filter-drawer', KpFilterDrawer);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-kp-filter-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.getAttribute('aria-controls'));
    if (drawer && drawer.open) drawer.open(opener);
  });

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-kp-drawer-open]');
    if (!opener) return;
    event.preventDefault();
    const drawer = document.getElementById('kp-drawer');
    if (drawer) drawer.open(opener);
  });

  /* ---------------------------------------------------------------------
     Desktop mega menus: click / Enter / Space toggles, hover-intent for
     fine pointers, Esc and outside click close.                         */
  const OPEN_DELAY = 150;
  const CLOSE_DELAY = 300;

  const initMegaMenu = (nav) => {
    if (!nav || nav.dataset.kpMegaReady) return;
    nav.dataset.kpMegaReady = 'true';
    const overlay = nav.parentElement.querySelector('[data-kp-mega-overlay]');
    let openToggle = null;
    let openTimer;
    let closeTimer;

    const panelFor = (toggle) => document.getElementById(toggle.getAttribute('aria-controls'));

    const close = (returnFocus = false) => {
      if (!openToggle) return;
      const toggle = openToggle;
      toggle.setAttribute('aria-expanded', 'false');
      toggle.closest('.item').classList.remove('mega-active');
      const panel = panelFor(toggle);
      if (panel) panel.hidden = true;
      if (overlay) overlay.hidden = true;
      openToggle = null;
      if (returnFocus) toggle.focus();
    };

    const open = (toggle) => {
      if (openToggle === toggle) return;
      close();
      const panel = panelFor(toggle);
      if (!panel) return;
      toggle.setAttribute('aria-expanded', 'true');
      toggle.closest('.item').classList.add('mega-active');
      panel.hidden = false;
      if (overlay) overlay.hidden = false;
      openToggle = toggle;
    };

    nav.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-kp-mega-toggle]');
      if (!toggle) return;
      event.preventDefault();
      if (openToggle === toggle) close();
      else open(toggle);
    });

    nav.querySelectorAll('.item').forEach((item) => {
      const toggle = item.querySelector('[data-kp-mega-toggle]');
      if (!toggle) return;
      item.addEventListener('mouseenter', () => {
        if (!finePointer.matches) return;
        clearTimeout(closeTimer);
        clearTimeout(openTimer);
        openTimer = setTimeout(() => open(toggle), openToggle ? 0 : OPEN_DELAY);
      });
      item.addEventListener('mouseleave', () => {
        if (!finePointer.matches) return;
        clearTimeout(openTimer);
        closeTimer = setTimeout(() => { if (openToggle === toggle) close(); }, CLOSE_DELAY);
      });
    });

    nav.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && openToggle) {
        event.preventDefault();
        close(true);
      }
    });

    nav.addEventListener('focusout', (event) => {
      if (openToggle && !nav.contains(event.relatedTarget)) close();
    });

    document.addEventListener('click', (event) => {
      if (openToggle && !nav.contains(event.target)) close();
    });

    if (overlay) overlay.addEventListener('click', () => close());
    window.addEventListener('resize', () => close(), { passive: true });
  };

  /* ---------------------------------------------------------------------
     Mobile header search toggle.                                        */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-kp-search-toggle]');
    if (!toggle) return;
    const target = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!target) return;
    const show = target.hidden || getComputedStyle(target).display === 'none';
    target.hidden = !show;
    target.closest('.kp-mh')?.classList.toggle('kp-mh--search-open', show);
    toggle.setAttribute('aria-expanded', String(show));
    if (show) {
      const input = target.querySelector('input[type="search"], input[name="q"]');
      if (input) input.focus();
    }
  });

  const init = (root = document) => root.querySelectorAll('[data-kp-megamenu]').forEach(initMegaMenu);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
